import React, { useEffect, useRef, useState } from 'react'
import { Form, Typography } from 'antd'
import { Formik } from 'formik'
import { RouteComponentProps, withRouter } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import { RequestStandardBilling } from 'src/types'

import { ApplicationState } from 'src/store'
import { Modal } from 'src/components/Ant'
import {
  getInitialValues,
  getValidationSchema
} from 'src/core/forms/request/billing'
import {
  resetSaveFullRequest,
  saveRequestStandardAssignments
} from 'src/core/store/ducks/requests/actions'
import useReasonPolicyRule from 'src/core/hooks/use-reason-policy-rule'
import useStandardResource from 'src/core/hooks/use-standard-resource'

import { WrapAlert } from './styles'
import FormFields from '../FormFields'

interface Params {
  reason_id: string
}

type Props = RouteComponentProps<Params> & {
  itemEdit?: RequestStandardBilling
  toggle(): void
  visible: boolean
}

const initialValues = getInitialValues()

function ModalForm({
  itemEdit,
  match: {
    params: { reason_id }
  },
  toggle,
  visible
}: Props) {
  const dispatch = useDispatch()
  const { requests } = useSelector((state: ApplicationState) => ({
    requests: state.requests
  }))

  const policyRule = useReasonPolicyRule(
    reason_id || requests.fullRequest?.request_user_data[0].reason_id
  )

  const [saving, setSaving] = useState<boolean>(false)

  const refFormik = useRef<Formik<RequestStandardBilling>>(null)

  const billingTypeStandard = useStandardResource('billing_types')
  const targetStandard = useStandardResource('targets')

  const { t } = useTranslation()

  useEffect(() => {
    if (visible) {
      setTimeout(() => {
        refFormik.current.resetForm(
          !itemEdit
            ? {
                ...initialValues,
                billing_type: billingTypeStandard,
                billing_type_id: billingTypeStandard
                  ? billingTypeStandard.billing_type_id.toString()
                  : null,
                target: targetStandard,
                target_id: targetStandard
                  ? targetStandard.target_id.toString()
                  : null
              }
            : {
                ...itemEdit,
                billing_type_id: itemEdit.billing_type_id.toString(),
                target_id: itemEdit.target_id.toString()
              }
        )
      }, 100)
    }
  }, [billingTypeStandard, itemEdit, targetStandard, visible])

  useEffect(() => {
    if (requests.saveFullRequestLoading !== saving) {
      setSaving(requests.saveFullRequestLoading)

      if (!requests.saveFullRequestLoading && !requests.saveFullRequestError) {
        toggle()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requests.saveFullRequestLoading])

  return (
    <Modal
      cancelButtonProps={{ disabled: saving }}
      closable={!saving}
      forceRender
      maskClosable={!saving}
      okButtonProps={{ loading: saving }}
      onCancel={() => {
        toggle()
        dispatch(resetSaveFullRequest())
      }}
      onOk={() => refFormik.current.submitForm()}
      title={
        <>
          {itemEdit
            ? t('web.assignment.billing.form.title-edit')
            : t('web.assignment.billing.form.title')}
          {!!itemEdit && (
            <Typography.Text code>
              # {itemEdit.request_standard_billing_id}
            </Typography.Text>
          )}
        </>
      }
      visible={visible}
      width={820}
    >
      <Formik
        ref={refFormik}
        initialValues={initialValues}
        onSubmit={values => {
          dispatch(
            saveRequestStandardAssignments({
              request_standard_billings: [values]
            })
          )
        }}
        validationSchema={getValidationSchema({ policyRule })}
        render={formikProps => (
          <Form onSubmit={formikProps.handleSubmit}>
            <FormFields
              enable_billing_observation={
                policyRule?.enable_billing_observation
              }
              formikProps={formikProps}
              organization_id={requests.fullRequest.organization_id}
            />
            {requests.saveFullRequestError && (
              <WrapAlert
                message={requests.saveFullRequestError}
                showIcon
                type="error"
              />
            )}
          </Form>
        )}
      />
    </Modal>
  )
}

export default withRouter(ModalForm)
